import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Chip, LinearProgress,
  Card, CardContent, CardActions, CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, Radio, RadioGroup, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as ATMIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const PLAN_ICONS = {
  free: <StarIcon />,
  basic: <StarIcon />,
  pro: <RocketIcon />,
  enterprise: <BusinessIcon />
};

const PLAN_COLORS = {
  free: '#9e9e9e',
  basic: '#2196f3',
  pro: '#ff9800',
  enterprise: '#9c27b0'
};

const STATUS_COLORS = {
  pending: 'warning',
  paid: 'success',
  failed: 'error',
  expired: 'default'
};

const STATUS_LABELS = {
  pending: '待付款',
  paid: '已付款',
  failed: '付款失敗',
  expired: '已過期'
};

const Billing = () => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // 付款 Dialog 狀態
  const [payDialog, setPayDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentType, setPaymentType] = useState('credit_card');
  const [submitting, setSubmitting] = useState(false);
  const [atmInfo, setAtmInfo] = useState(null);

  const formRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billingRes, plansRes, historyRes] = await Promise.all([
        api.get('/billing/current'),
        api.get('/billing/plans'),
        api.get('/billing/payment-history').catch(() => ({ data: { payments: [] } }))
      ]);

      if (billingRes.data.success) setBilling(billingRes.data.billing);
      if (plansRes.data.success) setPlans(plansRes.data.plans);
      if (historyRes.data.payments) setPaymentHistory(historyRes.data.payments);
    } catch (error) {
      toast.error(t('billing.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (plan.price_monthly > 0) {
      setSelectedPlan(plan);
      setBillingCycle('monthly');
      setPaymentType('credit_card');
      setAtmInfo(null);
      setPayDialog(true);
      return;
    }

    // 免費方案直接切換
    if (upgrading) return;
    setUpgrading(true);
    try {
      const res = await api.post('/billing/upgrade', { plan_id: plan.id });
      if (res.data.success) {
        toast.success(res.data.message || t('billing.upgrade_success'));
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('billing.upgrade_failed'));
    } finally {
      setUpgrading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!selectedPlan || submitting) return;
    setSubmitting(true);
    setAtmInfo(null);

    try {
      const res = await api.post('/billing/create-payment', {
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
        payment_type: paymentType
      });

      if (res.data.success) {
        if (paymentType === 'credit_card' && res.data.form_action) {
          // 信用卡：建立隱藏表單提交到 SmilePay
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = res.data.form_action;
          form.target = '_blank';

          Object.entries(res.data.form_data).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);

          toast.success('已開啟信用卡付款頁面');
          setPayDialog(false);
        } else if (paymentType === 'atm') {
          // ATM：顯示虛擬帳號
          setAtmInfo(res.data);
          toast.success('虛擬帳號已產生');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || '建立付款失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentAmount = () => {
    if (!selectedPlan) return 0;
    if (billingCycle === 'yearly' && selectedPlan.price_yearly) {
      return selectedPlan.price_yearly;
    }
    return billingCycle === 'yearly'
      ? Math.round(selectedPlan.price_monthly * 12 * 0.8)
      : selectedPlan.price_monthly;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const usagePercent = (used, limit) => {
    if (limit === -1) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const formatLimit = (val) => val === -1 ? t('billing.unlimited') : val.toLocaleString();

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {t('billing.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('billing.subtitle')}
      </Typography>

      {/* 試用期警告 */}
      {billing?.trial?.days_left !== null && billing?.trial?.days_left <= 7 && !billing?.subscription?.is_active && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          {billing.trial.is_expired
            ? t('billing.trial_expired_warning')
            : t('billing.trial_expiring_warning', { days: billing.trial.days_left })
          }
        </Alert>
      )}

      {/* 目前方案 + 使用量 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* 目前方案 */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('billing.current_plan')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ color: PLAN_COLORS[billing?.plan?.code] || '#666' }}>
                {PLAN_ICONS[billing?.plan?.code] || <StarIcon />}
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {billing?.plan?.name || t('billing.free_trial')}
              </Typography>
            </Box>
            {billing?.plan?.price_monthly > 0 && (
              <Typography variant="body2" color="text.secondary">
                NT$ {billing.plan.price_monthly}/月
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            {billing?.trial?.days_left !== null && !billing?.subscription?.is_active && (
              <Chip
                label={billing.trial.is_expired ? t('billing.trial_expired') : t('billing.trial_days_left', { days: billing.trial.days_left })}
                color={billing.trial.is_expired ? 'error' : billing.trial.days_left <= 3 ? 'warning' : 'success'}
                size="small"
                sx={{ mb: 1 }}
              />
            )}
            {billing?.subscription?.is_active && (
              <Chip
                label={t('billing.subscription_active', { days: billing.subscription.days_left })}
                color="success"
                size="small"
                sx={{ mb: 1 }}
              />
            )}
          </Paper>
        </Grid>

        {/* 使用量 */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('billing.usage')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {/* 商品數 */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('billing.product_count')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {billing?.usage?.products} / {formatLimit(billing?.usage?.products_limit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePercent(billing?.usage?.products, billing?.usage?.products_limit)}
                  sx={{
                    height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: usagePercent(billing?.usage?.products, billing?.usage?.products_limit) > 80 ? 'warning.main' : 'primary.main'
                    }
                  }}
                />
              </Box>

              {/* 本月訂單 */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('billing.monthly_order_count')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {billing?.usage?.orders_monthly} / {formatLimit(billing?.usage?.orders_limit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePercent(billing?.usage?.orders_monthly, billing?.usage?.orders_limit)}
                  sx={{
                    height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: usagePercent(billing?.usage?.orders_monthly, billing?.usage?.orders_limit) > 80 ? 'warning.main' : 'primary.main'
                    }
                  }}
                />
              </Box>

              {/* 儲存空間 */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('billing.storage')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Number(billing?.usage?.storage_mb || 0).toFixed(0)} MB / {formatLimit(billing?.usage?.storage_limit)} MB
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePercent(billing?.usage?.storage_mb, billing?.usage?.storage_limit)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 方案卡片 */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t('billing.choose_plan')}
      </Typography>
      <Grid container spacing={3}>
        {plans.map(plan => {
          const isCurrent = billing?.plan?.code === plan.code;
          const color = PLAN_COLORS[plan.code] || '#666';

          return (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isCurrent ? 2 : 1,
                  borderColor: isCurrent ? color : 'divider',
                  position: 'relative'
                }}
              >
                {isCurrent && (
                  <Chip
                    label={t('billing.current_plan')}
                    size="small"
                    sx={{
                      position: 'absolute', top: 12, right: 12,
                      bgcolor: color, color: 'white', fontWeight: 'bold'
                    }}
                  />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ color, mb: 1 }}>
                    {PLAN_ICONS[plan.code] || <StarIcon />}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {plan.price_monthly === 0 ? t('billing.free') : `$${plan.price_monthly}`}
                    {plan.price_monthly > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary">/{t('billing.per_month')}</Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2">
                        {t('billing.products')} {plan.max_products === -1 ? t('billing.unlimited') : `${plan.max_products} ${t('billing.items')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2">
                        {t('billing.monthly_orders')} {plan.max_orders_monthly === -1 ? t('billing.unlimited') : `${plan.max_orders_monthly} ${t('billing.orders_unit')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="body2">
                        {t('billing.storage')} {plan.max_storage_mb === -1 ? t('billing.unlimited') : `${plan.max_storage_mb} MB`}
                      </Typography>
                    </Box>
                    {plan.features?.analytics && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2">{t('billing.feature_analytics')}</Typography>
                      </Box>
                    )}
                    {plan.features?.custom_domain && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2">{t('billing.feature_custom_domain')}</Typography>
                      </Box>
                    )}
                    {plan.features?.priority_support && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2">{t('billing.feature_priority_support')}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {isCurrent ? (
                    <Button fullWidth variant="outlined" disabled>
                      {t('billing.current_plan')}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={upgrading}
                      onClick={() => handleUpgrade(plan)}
                      sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' } }}
                    >
                      {upgrading ? t('common.processing') : plan.price_monthly === 0 ? t('common.select') : t('billing.upgrade')}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* 付款紀錄 */}
      {paymentHistory.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> 付款紀錄
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>訂單號</TableCell>
                  <TableCell>方案</TableCell>
                  <TableCell>週期</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>付款方式</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>建立時間</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map(p => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.order_no}</TableCell>
                    <TableCell>{p.plan_name}</TableCell>
                    <TableCell>{p.billing_cycle === 'yearly' ? '年繳' : '月繳'}</TableCell>
                    <TableCell align="right">NT$ {p.amount?.toLocaleString()}</TableCell>
                    <TableCell>{p.payment_type === 'credit_card' ? '信用卡' : 'ATM'}</TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABELS[p.status] || p.status} color={STATUS_COLORS[p.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{new Date(p.created_at).toLocaleString('zh-TW')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* 付款 Dialog */}
      <Dialog open={payDialog} onClose={() => setPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          升級至 {selectedPlan?.name}
        </DialogTitle>
        <DialogContent>
          {/* ATM 帳號資訊 */}
          {atmInfo ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>虛擬帳號已產生，請於期限內完成轉帳</Alert>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">轉帳銀行</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>{atmInfo.bank_name || atmInfo.atm_bank_no}</Typography>

                <Typography variant="body2" color="text.secondary">虛擬帳號</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, fontFamily: 'monospace', letterSpacing: 2 }}>{atmInfo.atm_no}</Typography>

                <Typography variant="body2" color="text.secondary">應付金額</Typography>
                <Typography variant="h6" fontWeight="bold" color="error" sx={{ mb: 1 }}>NT$ {atmInfo.amount?.toLocaleString()}</Typography>

                {atmInfo.pay_end_date && (
                  <>
                    <Typography variant="body2" color="text.secondary">繳費期限</Typography>
                    <Typography variant="body1" fontWeight="bold">{new Date(atmInfo.pay_end_date).toLocaleString('zh-TW')}</Typography>
                  </>
                )}
              </Paper>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {/* 計費週期 */}
              <Typography variant="subtitle2" gutterBottom>計費週期</Typography>
              <ToggleButtonGroup
                value={billingCycle}
                exclusive
                onChange={(e, v) => v && setBillingCycle(v)}
                fullWidth
                sx={{ mb: 3 }}
              >
                <ToggleButton value="monthly">
                  月繳 NT$ {selectedPlan?.price_monthly?.toLocaleString()}
                </ToggleButton>
                <ToggleButton value="yearly">
                  年繳 NT$ {(selectedPlan?.price_yearly || Math.round(selectedPlan?.price_monthly * 12 * 0.8))?.toLocaleString()}
                  <Chip label="省20%" size="small" color="success" sx={{ ml: 1 }} />
                </ToggleButton>
              </ToggleButtonGroup>

              {/* 付款方式 */}
              <Typography variant="subtitle2" gutterBottom>付款方式</Typography>
              <RadioGroup value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                <FormControlLabel
                  value="credit_card"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCardIcon fontSize="small" />
                      <span>信用卡（Visa / MasterCard / JCB）</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="atm"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ATMIcon fontSize="small" />
                      <span>ATM 虛擬帳號轉帳</span>
                    </Box>
                  }
                />
              </RadioGroup>

              <Divider sx={{ my: 2 }} />

              {/* 金額摘要 */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">應付金額</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  NT$ {getPaymentAmount()?.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPayDialog(false); setAtmInfo(null); }}>
            {atmInfo ? '關閉' : '取消'}
          </Button>
          {!atmInfo && (
            <Button
              variant="contained"
              onClick={handlePaymentSubmit}
              disabled={submitting}
            >
              {submitting ? '處理中...' : paymentType === 'credit_card' ? '前往付款' : '取得虛擬帳號'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
